import { useEffect, useRef, useState } from "react";
import { Plus, Gift, Shuffle, Trash2, RotateCcw, PartyPopper } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { api, errMsg } from "../lib/api";
import { useStages } from "../lib/hooks";
import { Avatar, Empty, Field, Loader, PageHeader } from "../components/common";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";

const GiveawayForm = ({ open, onOpenChange, onSaved }) => {
  const stages = useStages();
  const [f, setF] = useState({ title: "", prize: "", stage_id: "", quantity: 1, pool: "stage" });
  const save = async (e) => {
    e.preventDefault();
    try {
      await api.post("/giveaways", { ...f, quantity: Number(f.quantity), stage_id: f.stage_id || null, pool: f.stage_id ? f.pool : "all" });
      toast.success("Sorteio criado"); onSaved(); onOpenChange(false);
      setF({ title: "", prize: "", stage_id: "", quantity: 1, pool: "stage" });
    } catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="border-[#232B3E] bg-[#121722] sm:max-w-md">
        <DialogHeader><DialogTitle className="h-display text-3xl">Novo sorteio</DialogTitle></DialogHeader>
        <form onSubmit={save} className="space-y-4">
          <Field label="Título"><input required data-testid="giveaway-title-input" className="inp" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} placeholder="Sorteio da 1ª Etapa" /></Field>
          <Field label="Brinde"><input required data-testid="giveaway-prize-input" className="inp" value={f.prize} onChange={(e) => setF({ ...f, prize: e.target.value })} placeholder="Bola oficial, camiseta..." /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Etapa (participantes)">
              <select data-testid="giveaway-stage-select" className="inp" value={f.stage_id} onChange={(e) => setF({ ...f, stage_id: e.target.value })}><option value="">Todos os atletas</option>{stages.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}</select>
            </Field>
            <Field label="Quantidade"><input data-testid="giveaway-quantity-input" type="number" min="1" className="inp" value={f.quantity} onChange={(e) => setF({ ...f, quantity: e.target.value })} /></Field>
          </div>
          <button data-testid="giveaway-save-btn" className="btn-lime w-full rounded-full py-3">Criar sorteio</button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const GiveawayCard = ({ g, isAdmin, onReload }) => {
  const [rolling, setRolling] = useState(null);
  const timer = useRef(null);
  useEffect(() => () => clearInterval(timer.current), []);
  const draw = async () => {
    try {
      const { data } = await api.post(`/giveaways/${g.id}/draw`);
      const pool = data.pool;
      let i = 0;
      timer.current = setInterval(() => { setRolling(pool[i % pool.length]?.name); i++; }, 80);
      setTimeout(() => {
        clearInterval(timer.current);
        setRolling(null);
        toast.success(`Ganhador(es): ${data.winners.map((w) => w.name).join(", ")}`);
        onReload();
      }, 2200);
    } catch (e) { toast.error(errMsg(e)); }
  };
  const reset = async () => { if (!window.confirm("Limpar ganhadores?")) return; await api.post(`/giveaways/${g.id}/reset`); onReload(); };
  const del = async () => { if (!window.confirm("Excluir sorteio?")) return; await api.delete(`/giveaways/${g.id}`); onReload(); };
  const done = g.winners.length >= g.quantity;
  return (
    <div data-testid={`giveaway-${g.id}`} className="card-x p-5">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="h-display text-3xl">{g.title}</div>
          <div className="flex items-center gap-1.5 text-sm text-[#E4F222]"><Gift className="h-4 w-4" />{g.quantity}x {g.prize}</div>
          <div className="mt-1 text-xs text-[#64748B]">{g.eligible_count} participante(s) elegível(is)</div>
        </div>
        {isAdmin && (
          <div className="flex gap-1">
            {g.winners.length > 0 && <button data-testid={`giveaway-reset-${g.id}`} onClick={reset} className="rounded-lg p-2 text-[#94A3B8] hover:bg-[#1A202E]"><RotateCcw className="h-4 w-4" /></button>}
            <button data-testid={`giveaway-delete-${g.id}`} onClick={del} className="rounded-lg p-2 text-[#94A3B8] hover:text-red-400"><Trash2 className="h-4 w-4" /></button>
          </div>
        )}
      </div>
      {rolling && <div data-testid="giveaway-rolling" className="h-display mt-5 rounded-xl bg-[#E4F222] py-6 text-center text-3xl text-black">{rolling}</div>}
      {!rolling && g.winners.length > 0 && (
        <div className="mt-4 space-y-2">
          <div className="eyebrow flex items-center gap-1"><PartyPopper className="h-3.5 w-3.5 text-[#E4F222]" />Ganhadores</div>
          {g.winners.map((w, i) => (
            <div key={`${w.user_id}-${i}`} data-testid={`winner-${w.user_id}`} className="flex items-center gap-3 rounded-xl border border-[#E4F222]/40 p-2.5">
              <span className="font-display text-xl font-black italic text-[#E4F222]">{i + 1}</span><Avatar user={w} size={34} /><span className="font-semibold">{w.name}</span>
            </div>
          ))}
        </div>
      )}
      {isAdmin && !done && !rolling && (
        <button data-testid={`giveaway-draw-${g.id}`} onClick={draw} className="btn-lime mt-5 flex w-full items-center justify-center gap-2 rounded-full py-3"><Shuffle className="h-4 w-4" />Sortear agora</button>
      )}
    </div>
  );
};

export default function Giveaways() {
  const { isAdmin } = useAuth();
  const [list, setList] = useState(null);
  const [open, setOpen] = useState(false);
  const load = () => api.get("/giveaways").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);
  return (
    <div>
      <PageHeader eyebrow="Prêmios" title="Sorteio de brindes" subtitle="Brindes sorteados entre os inscritos."
        action={isAdmin && <button data-testid="new-giveaway-btn" onClick={() => setOpen(true)} className="btn-lime flex items-center gap-2 rounded-full px-5 py-2.5 text-sm"><Plus className="h-4 w-4" />Novo sorteio</button>} />
      {!list ? <Loader /> : list.length === 0 ? <Empty icon={Gift} title="Nenhum sorteio" text="Os sorteios de brindes aparecerão aqui." /> : (
        <div className="grid gap-4 md:grid-cols-2">{list.map((g) => <GiveawayCard key={g.id} g={g} isAdmin={isAdmin} onReload={load} />)}</div>
      )}
      {isAdmin && <GiveawayForm open={open} onOpenChange={setOpen} onSaved={load} />}
    </div>
  );
}
